import { readFrame, reminderTime, type SessionReminderFrame } from './frame';
import { dismiss, dismissedSession, isDismissed, markNotified, pruneReminderStorage, wasNotified } from './storage';
import { createReminderToast } from './toast';

// Local view of the boot data avoids widening the separate bundles' ambient FateTide types.
type ReminderHost = Window & { FateTide?: {
    user?: { id?: string }; campaign?: { id?: string; campaign_id?: string }; sessionReminders?: unknown;
} };
const host = window as unknown as ReminderHost;
const toasts = new Map<string, { frame: SessionReminderFrame; toast: ReturnType<typeof createReminderToast>; expiry: number; notification?: Notification }>();
let container: HTMLDivElement | undefined;
let timer: number | undefined;

function suppressed(frame: SessionReminderFrame): boolean {
    const campaign = host.FateTide?.campaign;
    const current = String(campaign?.campaign_id || campaign?.id || '').toUpperCase();
    const start = Date.parse(frame.start_utc);
    return !host.FateTide?.user?.id || current === frame.campaign_id.toUpperCase()
        || isDismissed(frame.campaign_session_id) || Date.now() < start - 600000 || Date.now() >= start + 1800000;
}

function close(id: string): void {
    const entry = toasts.get(id);
    entry?.toast.root.remove();
    entry?.notification?.close();
    window.clearTimeout(entry?.expiry);
    toasts.delete(id);
    if (!toasts.size) {
        container?.remove();
        container = undefined;
        window.clearInterval(timer);
        timer = undefined;
    }
}

function notify(entry: NonNullable<ReturnType<typeof toasts.get>>): void {
    if (!document.hidden || typeof Notification === 'undefined' || Notification.permission !== 'granted'
        || wasNotified(entry.frame.campaign_session_id)) return;
    try {
        const notification = new Notification(entry.frame.campaign_name, {
            body: `${reminderTime(entry.frame)} — Go to campaign`, tag: entry.frame.campaign_session_id,
        });
        entry.notification = notification;
        markNotified(entry.frame.campaign_session_id);
        notification.onclick = () => {
            window.focus();
            window.location.assign(entry.frame.url);
            notification.close();
        };
    } catch { /* Some mobile browsers expose Notification but forbid its constructor. */ }
}

function refresh(): void {
    for (const [id, entry] of toasts) {
        if (suppressed(entry.frame)) close(id);
        else { entry.toast.update(entry.frame); notify(entry); }
    }
}

async function enableDesktopAlerts(): Promise<void> {
    try {
        if (typeof Notification !== 'undefined' && Notification.permission === 'default') await Notification.requestPermission();
    } catch { /* The in-site toast remains available if permission fails. */ }
    refresh();
}

function receive(value: unknown): void {
    const frame = readFrame(value);
    if (!frame) return;
    const id = frame.campaign_session_id;
    if (suppressed(frame)) { close(id); return; }
    const current = toasts.get(id);
    if (current) {
        window.clearTimeout(current.expiry);
        current.expiry = window.setTimeout(() => close(id), Date.parse(frame.start_utc) + 1800000 - Date.now());
        current.frame = frame;
        current.toast.update(frame);
        notify(current);
        return;
    }
    if (!container) {
        container = document.createElement('div');
        container.dataset.sessionReminderStack = 'ft.session-reminder.v1'; // Served-bytes smoke marker.
        Object.assign(container.style, {
            position: 'fixed', bottom: '16px', right: '16px', zIndex: '20040',
            width: 'min(24rem, calc(100vw - 32px))', maxHeight: 'calc(100dvh - 32px)',
            overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', pointerEvents: 'none',
        });
        document.body.append(container);
    }
    const toast = createReminderToast(frame, () => { dismiss(id); close(id); }, () => { void enableDesktopAlerts(); });
    const entry = { frame, toast, expiry: window.setTimeout(() => close(id), Date.parse(frame.start_utc) + 1800000 - Date.now()) };
    toasts.set(id, entry);
    container.append(toast.root);
    notify(entry);
    timer ??= window.setInterval(refresh, 30000);
}

function init(): void {
    pruneReminderStorage();
    window.addEventListener('ft:session-reminder', event => receive((event as CustomEvent<unknown>).detail));
    window.addEventListener('storage', event => {
        const id = dismissedSession(event);
        if (id) close(id);
    });
    document.addEventListener('visibilitychange', refresh);
    const pending = host.FateTide?.sessionReminders;
    if (Array.isArray(pending)) pending.forEach(receive);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
else init();
